/**
 * Tool definitions for presentation QA capabilities.
 * These tools allow Claude to capture and inspect presentation slides visually.
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages';

/**
 * Tool for capturing individual slide images from a rendered presentation.
 * Used for visual QA to inspect slides for overlapping elements, text overflow, etc.
 */
export const CAPTURE_SLIDES_TOOL: Tool = {
  name: 'capture_presentation_slides',
  description: `Capture individual slide images from a presentation for detailed visual QA inspection.

Use when you need close-up inspection of specific slides. Maximum 3 slides per call.
For full-deck overview, use create_thumbnail_grid instead.

Returns PNG images of the requested slides.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation to capture (from generate_presentation response)',
      },
      slideIndices: {
        type: 'array',
        items: { type: 'number' },
        maxItems: 3,
        description: '0-based indices of specific slides to capture. Required. Maximum 3 slides per call.',
      },
    },
    required: ['presentationId', 'slideIndices'],
  },
};

/**
 * Tool for creating a thumbnail grid showing all slides at once.
 * Useful for quick visual overview and editing workflow.
 */
export const CREATE_THUMBNAIL_TOOL: Tool = {
  name: 'create_thumbnail_grid',
  description: `Create a thumbnail grid showing all slides for quick overview.

Useful for:
- Quick visual overview of slide deck
- Editing workflow
- Before/after comparisons

Returns a single combined image with all slides in a grid.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      columns: {
        type: 'number',
        description: 'Number of columns in grid (default: 3)',
      },
    },
    required: ['presentationId'],
  },
};
